import { Document, Page, View, Text, StyleSheet, Font } from '@react-pdf/renderer';
import TablePageHeader from './TablePageHeader';
import TablePageFooter from './TablePageFooter';
import Header from './Header';
import Column from './Column';
import NestedColumn from './NestedColumn';
import { getFcrReporterDisplayName } from './utils/filters';
import Filters from './Filters';
import { Dayts } from '@/utils';
import { useMemo } from 'react';

export type PDFTableProps = {
  name: string;
  headers: Record<string, any>;
  filters?: Record<string, any>;
  rows: any[];
  orientation?: 'portrait' | 'landscape';
};

const PDFTable = ({ orientation = 'portrait', rows, headers, name, filters }: PDFTableProps): JSX.Element => {
  const columns = Object.keys(headers);
  const columnData = useMemo(() => {
    return rows.map((row) => {
      const columnData = columns.reduce((acc: Record<string, { name: string; values: any[] }>, key: string) => {
        if (key === 'provider') {
          acc[key] = { name: headers[key]?.displayName || 'Client', values: [row.provider?.displayName || '--'] };
        } else if (key === 'scheduled') {
          let value = '--';
          if (row[key]) {
            value = new Dayts(row[key]).formatAs(Dayts.Format.DATETIME_FE_HYPHENATED);
          }
          acc[key] = { name: typeof headers[key] === 'string' ? headers[key] : headers[key]?.displayName || key, values: [value] };
        } else if (key === 'fcrReporter') {
          acc[key] = { name: headers[key] || 'FCR Reporter', values: [getFcrReporterDisplayName(row?.[key]) || '--'] };
        } else {
          acc[key] = {
            name: typeof headers[key] === 'string' ? headers[key] : headers[key]?.displayName || key,
            values: [row[key] ?? '--'],
          };
        }
        return acc;
      }, {});
      let nestedColumnData: Array<{ title: string; value: any }> = [];
      if (row.children && Array.isArray(row.children) && row.children.length > 0) {
        nestedColumnData = row.children.map((child: any) => ({
          title: child.scheduled,
          value: child.airportCode ?? '--',
        }));
      }
      return {
        data: columnData,
        nestedData: nestedColumnData,
      };
    });
  }, [rows, columns, headers]);
  return (
    <Document>
      <Page size="A4" style={styles.page} orientation={orientation}>
        <TablePageHeader name={name} />
        <View style={styles.contentContainer}>
          {filters && Object.keys(filters).length > 0 && <Filters filters={filters} />}
          {rows.length > 0 && <Header headers={headers} fixed />}
          <View>
            {columnData.map((column, idx) => (
              <Columns {...column} key={idx} />
            ))}
          </View>
        </View>
        <View style={styles.pageCount} fixed>
          <Text style={styles.pageCountText} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
        <View style={styles.footerContainer}>
          <TablePageFooter />
        </View>
      </Page>
    </Document>
  );
};

Font.register({
  family: 'Gotham',
  fonts: [
    { src: '/public/assets/fonts/Gotham/GothamPro.ttf' },
    { src: '/public/assets/fonts/Gotham/GothamPro-Italic.ttf', fontStyle: 'italic' },
    { src: '/public/assets/fonts/Gotham/GothamPro-Medium.ttf', fontStyle: 'normal', fontWeight: 350 },
    { src: '/public/assets/fonts/Gotham/GothamPro-Bold.ttf', fontStyle: 'normal', fontWeight: 700 },
    { src: '/public/assets/fonts/Gotham/GothamPro-Black.ttf', fontStyle: 'normal', fontWeight: 900 },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    minHeight: '100%',
    paddingTop: 10,
    fontFamily: 'Gotham',
  },
  contentContainer: {
    flexDirection: 'column',
    flexGrow: 1,
    padding: '0 14px 18px',
  },
  footerContainer: {
    marginTop: 'auto',
  },
  pageCount: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: 5,
    marginTop: 5,
  },
  pageCountText: {
    fontSize: 8,
    fontWeight: 'bold',
  },
});

const Columns = ({ data, nestedData }) => {
  return (
    <View wrap={false}>
      <Column column={data} showHeader={false} />
      {nestedData.length > 0 && <NestedColumn rows={nestedData} />}
    </View>
  );
};

export default PDFTable;
